/**
 * Drag to reorder — pointer based (mouse, pen and touch alike), with a live
 * preview of where the item will land: the dragged item follows the pointer
 * and its neighbours slide aside to open the gap it will drop into. Shared by
 * every Pinned list and the macOS Dock.
 *
 * A press that does not move past a few pixels is an ordinary click; after a
 * real drag the click that follows is swallowed. Esc cancels. Keyboard reorder
 * (the up/down buttons, Alt+Shift+arrows) stays the accessible path.
 *
 * Direct DOM transforms while the pointer moves — nothing re-renders per move.
 */
import { useCallback, useEffect, useRef } from 'react'

export interface DragReorderOptions {
  axis: 'x' | 'y'
  /** The element holding the items. */
  containerRef: React.RefObject<HTMLElement | null>
  /** Move item `key` to position `to` (0-based, in the current order). */
  onMove: (key: string, to: number) => void
  /** Called as a drag starts / ends (pause magnification, hide tooltips). */
  onDragState?: (dragging: boolean) => void
  /** px the pointer must travel before a press becomes a drag. */
  threshold?: number
  disabled?: boolean
}

const ATTR = 'data-reorder-key'

type Active = {
  key: string
  el: HTMLElement
  items: HTMLElement[]
  /** Each item's own inline transition, put back when the drag ends. */
  transitions: string[]
  starts: number[]
  sizes: number[]
  step: number
  from: number
  to: number
  origin: number
  started: boolean
  pointerId: number
}

export function useDragReorder(opts: DragReorderOptions) {
  const optsRef = useRef(opts)
  optsRef.current = opts
  const act = useRef<Active | null>(null)

  // One set of window handlers for the life of the component, so adding and
  // removing them always names the same functions.
  const h = useRef<{
    move: (e: PointerEvent) => void
    up: (e: PointerEvent) => void
    cancel: () => void
    key: (e: KeyboardEvent) => void
    finish: (commit: boolean) => void
  } | null>(null)
  if (!h.current) {
    const resetStyles = (a: Active) => {
      a.items.forEach((it, i) => {
        it.style.transition = 'none'
        it.style.transform = ''
        it.style.zIndex = ''
        it.style.opacity = ''
        it.style.boxShadow = ''
        it.style.cursor = ''
        // Give the item its own transition back once the snap has applied.
        void it.offsetWidth
        it.style.transition = a.transitions[i]
      })
    }
    const finish = (commit: boolean) => {
      const a = act.current
      if (!a) return
      window.removeEventListener('pointermove', h.current!.move)
      window.removeEventListener('pointerup', h.current!.up)
      window.removeEventListener('pointercancel', h.current!.cancel)
      window.removeEventListener('keydown', h.current!.key, true)
      document.body.style.userSelect = ''
      act.current = null
      if (!a.started) return
      // The click that ends a drag is not a click on the item.
      const swallow = (ev: Event) => { ev.stopPropagation(); ev.preventDefault() }
      window.addEventListener('click', swallow, { capture: true, once: true })
      setTimeout(() => window.removeEventListener('click', swallow, true), 0)
      resetStyles(a)
      if (commit && a.to !== a.from) optsRef.current.onMove(a.key, a.to)
      optsRef.current.onDragState?.(false)
    }
    const move = (e: PointerEvent) => {
      const a = act.current
      if (!a || e.pointerId !== a.pointerId) return
      const axis = optsRef.current.axis
      const pos = axis === 'x' ? e.clientX : e.clientY
      const delta = pos - a.origin
      if (!a.started) {
        if (Math.abs(delta) < (optsRef.current.threshold ?? 5)) return
        a.started = true
        optsRef.current.onDragState?.(true)
        for (const it of a.items) it.style.transition = it === a.el ? 'none' : 'transform .16s ease'
        a.el.style.zIndex = '5'
        a.el.style.opacity = '0.92'
        a.el.style.boxShadow = '0 8px 22px rgba(0,0,0,0.25)'
        a.el.style.cursor = 'grabbing'
      }
      e.preventDefault()
      const prop = axis === 'x' ? 'translateX' : 'translateY'
      a.el.style.transform = `${prop}(${delta}px)`
      // Where the dragged item's centre now falls among the others' rest centres.
      const centre = a.starts[a.from] + a.sizes[a.from] / 2 + delta
      let to = a.from
      for (let i = 0; i < a.items.length; i += 1) {
        if (i === a.from) continue
        const c = a.starts[i] + a.sizes[i] / 2
        if (i < a.from && centre < c) to = Math.min(to, i)
        if (i > a.from && centre > c) to = Math.max(to, i)
      }
      a.to = to
      // The items in between slide by the dragged item's footprint, opening
      // the gap it will drop into.
      a.items.forEach((it, i) => {
        if (i === a.from) return
        const shift = (a.from < to && i > a.from && i <= to) ? -a.step
          : (a.from > to && i >= to && i < a.from) ? a.step : 0
        it.style.transform = shift ? `${prop}(${shift}px)` : ''
      })
    }
    h.current = {
      move,
      up: (e: PointerEvent) => { if (act.current && e.pointerId === act.current.pointerId) finish(true) },
      cancel: () => finish(false),
      key: (e: KeyboardEvent) => {
        if (e.key === 'Escape' && act.current?.started) { e.preventDefault(); e.stopPropagation(); finish(false) }
      },
      finish,
    }
  }

  useEffect(() => () => h.current?.finish(false), [])

  /** Spread onto each item: `{...bind(key)}` (adds the key, the press handler
   *  and a touch-action that leaves the cross axis to scrolling). */
  const bind = useCallback((key: string) => ({
    [ATTR]: key,
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      const o = optsRef.current
      if (o.disabled || e.button !== 0 || act.current) return
      // Controls inside an item (move up / down, unpin) are never a drag handle.
      if ((e.target as HTMLElement).closest('[data-no-drag]')) return
      const box = o.containerRef.current
      if (!box) return
      const items = [...box.querySelectorAll<HTMLElement>(`[${ATTR}]`)]
      const el = items.find((it) => it.getAttribute(ATTR) === key)
      if (!el) return
      const rects = items.map((it) => it.getBoundingClientRect())
      const starts = rects.map((r) => (o.axis === 'x' ? r.left : r.top))
      const sizes = rects.map((r) => (o.axis === 'x' ? r.width : r.height))
      const from = items.indexOf(el)
      // The footprint of one slot: the item plus the gap to its neighbour.
      const gap = items.length > 1
        ? (from + 1 < items.length ? starts[from + 1] - (starts[from] + sizes[from]) : starts[from] - (starts[from - 1] + sizes[from - 1]))
        : 0
      act.current = {
        key, el, items, transitions: items.map((it) => it.style.transition), starts, sizes, from, to: from,
        step: sizes[from] + Math.max(0, gap),
        origin: o.axis === 'x' ? e.clientX : e.clientY, started: false, pointerId: e.pointerId,
      }
      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', h.current!.move, { passive: false })
      window.addEventListener('pointerup', h.current!.up)
      window.addEventListener('pointercancel', h.current!.cancel)
      window.addEventListener('keydown', h.current!.key, true)
    },
  }), [])

  return { bind, isDragging: () => !!act.current?.started }
}
